'use client'

import React from 'react'
import Link from 'next/link'
import { Button, type ButtonProps } from '@mantine/core'

export type CtaVariant = 'primary' | 'primary-white' | 'outline' | 'outline-dark'

interface RefreshCtaButtonProps extends Omit<ButtonProps, 'variant' | 'onClick'> {
    href?: string
    variant?: CtaVariant
    children: React.ReactNode
    onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
    /** Forwarded when `href` is set (e.g. `_blank` for external URLs). */
    target?: React.HTMLAttributeAnchorTarget
    rel?: string
    prefetch?: boolean
}

type VariantStyle = {
    variant: ButtonProps['variant']
    bg?: string
    c?: string
    rootStyles?: React.CSSProperties
}

const variantStyles: Record<CtaVariant, VariantStyle> = {
    primary: {
        variant: 'scale-hover',
        bg: 'blue.6',
        c: 'white',
    },
    'primary-white': {
        variant: 'scale-hover',
        bg: 'white',
        c: 'gray.9',
    },
    outline: {
        variant: 'outline-scale-hover',
        bg: 'transparent',
        c: 'gray.9',
        rootStyles: { borderColor: 'var(--gm-fg-gray, #878685)' },
    },
    'outline-dark': {
        variant: 'outline-scale-hover',
        c: 'white',
        rootStyles: {
            background: 'linear-gradient(169deg, #0C0E1C 2.03%, #0F1227 40.08%, #1B1D48 106.16%)',
        },
    },
}

/** Shared CTA button for CategoryPages – reduces repeated px, py, radius, fz, fw, lh props */
export function RefreshCtaButton({
    href,
    variant = 'primary',
    children,
    leftSection,
    styles: customStyles,
    target,
    rel,
    prefetch = true,
    ...rest
}: RefreshCtaButtonProps) {
    const { variant: mantineVariant, bg, c, rootStyles: variantRootStyles } = variantStyles[variant]
    const rootStyles: React.CSSProperties = {
        flexShrink: 0,
        minHeight: 48,
        ...variantRootStyles,
        ...(customStyles && typeof customStyles === 'object' && 'root' in customStyles
            ? (customStyles.root as React.CSSProperties)
            : {}),
    }

    if (href) {
        return (
            <Button
                component={Link}
                href={href}
                prefetch={prefetch}
                target={target}
                rel={rel}
                variant={mantineVariant}
                bg={bg}
                c={c}
                px={24}
                py={16}
                radius={999}
                size="sm"
                fw={550}
                lh={1.4}
                h="auto"
                styles={{ root: rootStyles }}
                leftSection={leftSection}
                {...rest}
            >
                {children}
            </Button>
        )
    }

    return (
        <Button
            variant={mantineVariant}
            bg={bg}
            c={c}
            px={24}
            py={16}
            radius={999}
            size="sm"
            fw={550}
            lh={1.4}
            h="auto"
            styles={{ root: rootStyles }}
            leftSection={leftSection}
            {...rest}
        >
            {children}
        </Button>
    )
}
